import { useCallback } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Button, Input } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { prepareHubspotData, sendToAmplitude, sendToHubspot, useFormData } from '../../model';
import s from './index.module.css';

type FormData = { emails: { to_invite: string }[] };

export const Invite = () => {
    const { email, nextStep } = useFormData();

    const methods = useForm<FormData>({
        values: {
            emails: [{ to_invite: '' }]
        }
    });
    const { control, watch, handleSubmit: handleSubmitHook } = methods;
    const emailsList = watch('emails');
    const nextEnabled = emailsList.some(item => !!item.to_invite);

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'emails'
    });

    const handleBack = useCallback(() => nextStep('role'), []);

    const handleSkip = useCallback(() => {
        sendToAmplitude(`WALLARMCOM_AASM_INVITE_SKIP`);
        nextStep('solutions');
    }, []);

    const handleAdd = useCallback(() => {
        append({ to_invite: '' });
        sendToAmplitude(`WALLARMCOM_AASM_INVITE_ADD_MORE`);
    }, []);

    const handleSubmit = useCallback(async (data: FormData) => {
        const list = data.emails
            .map(item => item.to_invite)
            .filter(item => !!item)
            .join(', ');

        sendToAmplitude(`WALLARMCOM_AASM_INVITE_LIST_SUBMITTED`, { to_invite: list });
        await sendToHubspot(
            prepareHubspotData({
                email,
                to_invite: list
            })
        );

        nextStep('solutions');
    }, []);

    return (
        <>
            <p>
                We suggest inviting teammates from Security, Software and DevOps teams. Inviting a
                teammate you extend your trial for extra 7 days (up to 30 days).
            </p>

            <form onSubmit={handleSubmitHook(handleSubmit)}>
                {fields.map(({ id }, index) => (
                    <div key={id} className={s.row}>
                        <Controller
                            name={`emails.${index}.to_invite`}
                            control={control}
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    size='large'
                                    placeholder='Email'
                                    onBlur={() => sendToAmplitude(`WALLARMCOM_AASM_INVITE_EMAIL`)}
                                />
                            )}
                        />

                        {fields.length > 1 && (
                            <MinusCircleOutlined
                                className={s.remove}
                                size={24}
                                onClick={() => remove(index)}
                            />
                        )}
                    </div>
                ))}

                <Button size='large' onClick={handleAdd}>
                    Add another
                </Button>
            </form>

            <div className={s.actions}>
                <Button size='large' onClick={handleBack}>
                    Back
                </Button>
                <div className={s.end}>
                    <Button type='text' size='large' className={s.skip} onClick={handleSkip}>
                        Skip for now
                    </Button>
                    <Button
                        type='primary'
                        size='large'
                        htmlType='submit'
                        disabled={!nextEnabled}
                        onClick={handleSubmitHook(handleSubmit)}
                    >
                        Invite teammates
                    </Button>
                </div>
            </div>
        </>
    );
};
